import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
	catchError,
	distinctUntilChanged,
	from,
	map,
	of,
	switchMap,
	filter,
	withLatestFrom,
} from 'rxjs';
import * as StateAdviceActions from './advice.actions';
import {
	getStateAdviceAllArticles,
	getStateAdviceCategories,
	getStateAdviceOffset,
	getStateAdvicePage,
	getStateAdviceSearchTerm,
} from './advice.selectors';
import { AdviceArticleApiService } from '@uc/web/shared/data-access';
import { AdviceApiService } from '../advice-api.service';
import { AdviceService } from '../../services/advice.service';

@Injectable()
export class StateAdviceEffects {
	constructor(
		private readonly _actions$: Actions,
		private _store: Store,
		private _adviceSrv: AdviceService,
		private _adviceApiSrv: AdviceApiService,
		private _adviceArticleApiSrv: AdviceArticleApiService,
	) {}

	fetchAllArticles = createEffect(() =>
		this._actions$.pipe(
			ofType(StateAdviceActions.fetchAllArticles),
			withLatestFrom(this._store.select(getStateAdviceAllArticles)),
			distinctUntilChanged(),
			filter(([, articles]) => !articles),
			switchMap(() =>
				from(this._adviceApiSrv.getAllArticles()).pipe(
					map((response) =>
						StateAdviceActions.updateAllArticles({
							articles: response,
						}),
					),
					catchError((error) =>
						of(StateAdviceActions.loadAdviceFailure(error)),
					),
				),
			),
		),
	);

	//general featured (non-category specific)
	fetchFeatured = createEffect(() =>
		this._actions$.pipe(
			ofType(StateAdviceActions.fetchFeatured),
			distinctUntilChanged(),
			switchMap(() =>
				from(this._adviceApiSrv.getFeaturedArticle()).pipe(
					map((response) =>
						StateAdviceActions.updateFeatured({
							featured: response,
						}),
					),
					catchError((error) =>
						of(StateAdviceActions.loadAdviceFailure(error)),
					),
				),
			),
		),
	);

	//featured (category-specific)
	fetchFeaturedWithCategoryId = createEffect(() =>
		this._actions$.pipe(
			ofType(StateAdviceActions.fetchFeaturedWithCategory),
			concatLatestFrom(() => this._store.select(getStateAdviceCategories)), //select categories from store / DB
			switchMap(([params, categories]) => {
				const { categoryKey } = params;
				if (categories) {
					const catId = this._adviceSrv.getCategoryId(categories, categoryKey);
					return of(catId);
				} else {
					return this._adviceApiSrv.getAdviceCategories().pipe(
						map((response) => {
							StateAdviceActions.updateCategories({
								categories: response,
							});
							const catId = this._adviceSrv.getCategoryId(
								response,
								categoryKey,
							);
							return catId;
						}),
						catchError((error) =>
							of(StateAdviceActions.loadAdviceFailure(error)),
						),
					);
				}
			}),
			switchMap(
				(
					catId, //get category related info from DB and update state
				) =>
					from(this._adviceApiSrv.getFeaturedArticle(catId)).pipe(
						map((response) =>
							StateAdviceActions.updateFeatured({
								featured: response,
							}),
						),
						catchError((error) =>
							of(StateAdviceActions.loadAdviceFailure(error)),
						),
					),
			),
		),
	);

	// get single article
	fetchSingleArticle = createEffect(() =>
		this._actions$.pipe(
			ofType(StateAdviceActions.fetchSingleArticle),
			concatLatestFrom(() => this._store.select(getStateAdviceAllArticles)), //select all articles from store
			switchMap(([params, articles]) => {
				const { categoryUrl, articleUrl } = params;

				if (articles) {
					//articles present in store
					const artId = this._adviceSrv.getArticleId(
						articles,
						categoryUrl,
						articleUrl,
					);
					return of(artId);
				} else {
					//no articles in store, fetch from DB & update store
					return this._adviceApiSrv.getAllArticles().pipe(
						map((response) => {
							StateAdviceActions.updateAllArticles({
								articles: response,
							});

							const artId = this._adviceSrv.getArticleId(
								response,
								categoryUrl,
								articleUrl,
							);
							return artId;
						}),
						catchError((error) =>
							of(StateAdviceActions.loadAdviceFailure(error)),
						),
					);
				}
			}),
			switchMap(
				(
					id, //with ID get the article content from DB & update store
				) =>
					from(this._adviceArticleApiSrv.getIndividualArticle(id)).pipe(
						map((response) =>
							StateAdviceActions.updateSingleArticle({
								singleArticle: response,
							}),
						),
						catchError((error) =>
							of(StateAdviceActions.loadAdviceFailure(error)),
						),
					),
			),
		),
	);

	//search
	fetchSearchArticles = createEffect(() =>
		this._actions$.pipe(
			ofType(StateAdviceActions.fetchSearchAdvice),
			concatLatestFrom(() => [
				this._store.select(getStateAdviceSearchTerm),
				this._store.select(getStateAdvicePage),
				this._store.select(getStateAdviceOffset),
			]),
			switchMap((params) =>
				from(
					this._adviceApiSrv.searchArticles(
						params[0].search,
						params[0].page,
						params[0].offset,
					),
				).pipe(
					map((response) => {
						return StateAdviceActions.updateSearchAdvice({
							search: response,
						});
					}),
					catchError((error) =>
						of(StateAdviceActions.loadAdviceFailure(error)),
					),
				),
			),
		),
	);
}
